// frontend/src/components/MobileUIOptimizer.jsx
// Mobile UI optimizer for responsive design and touch-friendly interactions

import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";

const MobileUIOptimizer = ({
  children,
  className = "",
  enableAutoOptimization = true,
  enableTouchOptimization = true,
  enableResponsiveLayout = true,
  enableSafeAreaSupport = true,
  enableHapticFeedback = true,
  showDebugPanel = false, // New prop to control debug panel visibility
}) => {
  const { t } = useTranslation();

  const [deviceInfo, setDeviceInfo] = useState({
    isMobile: false,
    isTablet: false,
    isDesktop: false,
    screenSize: "desktop",
    orientation: "portrait",
    hasTouch: false,
    hasHaptic: false,
    safeAreaInsets: { top: 0, right: 0, bottom: 0, left: 0 },
  });

  const [optimizations, setOptimizations] = useState({
    touchTargets: false,
    responsiveLayout: false,
    safeArea: false,
    hapticFeedback: false,
  });

  // Detect device capabilities
  const detectDevice = useCallback(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    const isMobile = /mobile|android|iphone|ipad|phone/i.test(userAgent);
    const isTablet = /tablet|ipad/i.test(userAgent);
    const isDesktop = !isMobile && !isTablet;

    const hasTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;
    const hasHaptic = "vibrate" in navigator;

    const width = window.innerWidth;
    const height = window.innerHeight;
    const orientation = width > height ? "landscape" : "portrait";

    let screenSize = "desktop";
    if (width < 768) screenSize = "mobile";
    else if (width < 1024) screenSize = "tablet";

    // Calculate safe area insets (approximation)
    const safeAreaInsets = {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    };

    // iOS safe area detection
    if (CSS.supports("padding-top", "env(safe-area-inset-top)")) {
      const style = getComputedStyle(document.documentElement);
      safeAreaInsets.top = parseInt(style.getPropertyValue("--sat") || "0");
      safeAreaInsets.right = parseInt(style.getPropertyValue("--sar") || "0");
      safeAreaInsets.bottom = parseInt(style.getPropertyValue("--sab") || "0");
      safeAreaInsets.left = parseInt(style.getPropertyValue("--sal") || "0");
    }

    setDeviceInfo({
      isMobile,
      isTablet,
      isDesktop,
      screenSize,
      orientation,
      hasTouch,
      hasHaptic,
      safeAreaInsets,
    });
  }, []);

  // Apply optimizations based on device
  const applyOptimizations = useCallback(() => {
    if (!enableAutoOptimization) return;

    const newOptimizations = {
      touchTargets: enableTouchOptimization && deviceInfo.hasTouch,
      responsiveLayout: enableResponsiveLayout,
      safeArea:
        enableSafeAreaSupport && (deviceInfo.isMobile || deviceInfo.isTablet),
      hapticFeedback: enableHapticFeedback && deviceInfo.hasHaptic,
    };

    setOptimizations(newOptimizations);

    // Apply CSS custom properties
    const root = document.documentElement;
    root.style.setProperty(
      "--touch-target-size",
      newOptimizations.touchTargets ? "44px" : "auto"
    );
    root.style.setProperty(
      "--safe-area-top",
      `${deviceInfo.safeAreaInsets.top}px`
    );
    root.style.setProperty(
      "--safe-area-right",
      `${deviceInfo.safeAreaInsets.right}px`
    );
    root.style.setProperty(
      "--safe-area-bottom",
      `${deviceInfo.safeAreaInsets.bottom}px`
    );
    root.style.setProperty(
      "--safe-area-left",
      `${deviceInfo.safeAreaInsets.left}px`
    );

    // Add device-specific classes to body
    document.body.classList.toggle("mobile-device", deviceInfo.isMobile);
    document.body.classList.toggle("tablet-device", deviceInfo.isTablet);
    document.body.classList.toggle("desktop-device", deviceInfo.isDesktop);
    document.body.classList.toggle("touch-device", deviceInfo.hasTouch);
    document.body.classList.toggle("haptic-device", deviceInfo.hasHaptic);
  }, [
    enableAutoOptimization,
    enableTouchOptimization,
    enableResponsiveLayout,
    enableSafeAreaSupport,
    enableHapticFeedback,
    deviceInfo,
  ]);

  // Handle orientation change
  const handleOrientationChange = useCallback(() => {
    setTimeout(detectDevice, 100); // Small delay to ensure dimensions are updated
  }, [detectDevice]);

  // Handle resize
  const handleResize = useCallback(() => {
    detectDevice();
  }, [detectDevice]);

  // Initialize device detection
  useEffect(() => {
    detectDevice();

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleOrientationChange);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleOrientationChange);
    };
  }, [detectDevice, handleResize, handleOrientationChange]);

  // Apply optimizations when device info changes
  useEffect(() => {
    applyOptimizations();
  }, [applyOptimizations]);

  // Haptic feedback utility
  const triggerHapticFeedback = useCallback(
    (pattern = 50) => {
      if (optimizations.hapticFeedback && navigator.vibrate) {
        navigator.vibrate(pattern);
      }
    },
    [optimizations.hapticFeedback]
  );

  // Touch target size utility
  const getTouchTargetSize = useCallback(() => {
    return optimizations.touchTargets ? 44 : "auto";
  }, [optimizations.touchTargets]);

  // Safe area utility
  const getSafeAreaStyle = useCallback(
    (position = "all") => {
      if (!optimizations.safeArea) return {};

      const { safeAreaInsets } = deviceInfo;

      switch (position) {
        case "top":
          return { paddingTop: safeAreaInsets.top };
        case "bottom":
          return { paddingBottom: safeAreaInsets.bottom };
        case "left":
          return { paddingLeft: safeAreaInsets.left };
        case "right":
          return { paddingRight: safeAreaInsets.right };
        case "horizontal":
          return {
            paddingLeft: safeAreaInsets.left,
            paddingRight: safeAreaInsets.right,
          };
        case "vertical":
          return {
            paddingTop: safeAreaInsets.top,
            paddingBottom: safeAreaInsets.bottom,
          };
        default:
          return {
            paddingTop: safeAreaInsets.top,
            paddingRight: safeAreaInsets.right,
            paddingBottom: safeAreaInsets.bottom,
            paddingLeft: safeAreaInsets.left,
          };
      }
    },
    [optimizations.safeArea, deviceInfo]
  );

  // Responsive breakpoint utility
  const isBreakpoint = useCallback(
    (breakpoint) => {
      const breakpoints = {
        mobile: deviceInfo.screenSize === "mobile",
        tablet: deviceInfo.screenSize === "tablet",
        desktop: deviceInfo.screenSize === "desktop",
        small: deviceInfo.screenSize === "mobile",
        medium: deviceInfo.screenSize === "tablet",
        large: deviceInfo.screenSize === "desktop",
      };

      return breakpoints[breakpoint] || false;
    },
    [deviceInfo.screenSize]
  );

  const contextValue = {
    // Device info
    deviceInfo,
    optimizations,

    // Utilities
    triggerHapticFeedback,
    getTouchTargetSize,
    getSafeAreaStyle,
    isBreakpoint,
  };

  return (
    <div
      className={`mobile-ui-optimizer ${className}`}
      style={{
        "--touch-target-size": getTouchTargetSize(),
        "--safe-area-top": `${deviceInfo.safeAreaInsets.top}px`,
        "--safe-area-right": `${deviceInfo.safeAreaInsets.right}px`,
        "--safe-area-bottom": `${deviceInfo.safeAreaInsets.bottom}px`,
        "--safe-area-left": `${deviceInfo.safeAreaInsets.left}px`,
      }}
    >
      {children}

      {/* Debug panel (only in development) */}
      {import.meta.env.MODE === "development" && showDebugPanel && (
        <div className="fixed top-4 left-4 z-50 bg-black bg-opacity-75 text-white p-3 rounded text-xs max-w-64">
          <h3 className="font-bold mb-2">Mobile UI Optimizer</h3>
          <div className="space-y-1">
            <div>
              Device:{" "}
              {deviceInfo.isMobile
                ? "Mobile"
                : deviceInfo.isTablet
                ? "Tablet"
                : "Desktop"}
            </div>
            <div>Screen: {deviceInfo.screenSize}</div>
            <div>Orientation: {deviceInfo.orientation}</div>
            <div>Touch: {deviceInfo.hasTouch ? "Yes" : "No"}</div>
            <div>Haptic: {deviceInfo.hasHaptic ? "Yes" : "No"}</div>
          </div>

          <div className="mt-2 pt-2 border-t border-gray-600">
            <div className="font-semibold mb-1">Optimizations:</div>
            <div className="space-y-1">
              <div>
                Touch Targets: {optimizations.touchTargets ? "✅" : "❌"}
              </div>
              <div>
                Responsive: {optimizations.responsiveLayout ? "✅" : "❌"}
              </div>
              <div>Safe Area: {optimizations.safeArea ? "✅" : "❌"}</div>
              <div>Haptic: {optimizations.hapticFeedback ? "✅" : "❌"}</div>
            </div>
          </div>

          <div className="mt-2 pt-2 border-t border-gray-600">
            <div className="font-semibold mb-1">Safe Area:</div>
            <div className="text-xs">
              T: {deviceInfo.safeAreaInsets.top}px
              <br />
              R: {deviceInfo.safeAreaInsets.right}px
              <br />
              B: {deviceInfo.safeAreaInsets.bottom}px
              <br />
              L: {deviceInfo.safeAreaInsets.left}px
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileUIOptimizer;
